import type { TqlAstNode, TqlFieldExpression } from './types';
import { parseTql } from './parser';
import { type PresetResolver } from './evaluator';
import {
  BODY_TQL_FIELDS,
  RESPONSE_BODY_TQL_FIELDS,
  TQL_BODY_FIELD_TO_COLUMN,
} from './body-field-mapping';

const BODY_FIELDS = BODY_TQL_FIELDS;
const RESPONSE_BODY_FIELDS = RESPONSE_BODY_TQL_FIELDS;

const NOOP_SQL: SqlTranslation = { whereClause: '1=1', params: [] };

export type SqlTranslation = {
  whereClause: string;
  params: (string | number)[];
};

export type RequestSqlTranslation = {
  whereClause: string;
  params: (string | number)[];
  requiresBodyJoin: boolean;
};

// ---------------------------------------------------------------------------
// Shared operator translators
// ---------------------------------------------------------------------------

function escapeLike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function translateStringOperator(
  column: string,
  operator: string,
  value: unknown,
): SqlTranslation {
  const str = String(value ?? '').toLowerCase();

  switch (operator) {
    case 'cont':
      return {
        whereClause: `LOWER(${column}) LIKE ? ESCAPE '\\'`,
        params: [`%${escapeLike(str)}%`],
      };
    case 'ncont':
      return {
        whereClause: `(${column} IS NULL OR LOWER(${column}) NOT LIKE ? ESCAPE '\\')`,
        params: [`%${escapeLike(str)}%`],
      };
    case 'eq':
      return { whereClause: `LOWER(${column}) = ?`, params: [str] };
    case 'neq':
      return {
        whereClause: `(${column} IS NULL OR LOWER(${column}) != ?)`,
        params: [str],
      };
    case 'starts':
      return {
        whereClause: `LOWER(${column}) LIKE ? ESCAPE '\\'`,
        params: [`${escapeLike(str)}%`],
      };
    case 'ends':
      return {
        whereClause: `LOWER(${column}) LIKE ? ESCAPE '\\'`,
        params: [`%${escapeLike(str)}`],
      };
    case 'regex':
      return {
        whereClause: `regexp(?, ${column})`,
        params: [String(value ?? '')],
      };
    case 'exists':
      return {
        whereClause: `(${column} IS NOT NULL AND ${column} != '')`,
        params: [],
      };
    case 'nexists':
      return {
        whereClause: `(${column} IS NULL OR ${column} = '')`,
        params: [],
      };
    default:
      return NOOP_SQL;
  }
}

function translateNumericOperator(
  column: string,
  operator: string,
  value: unknown,
): SqlTranslation {
  if (operator === 'exists') {
    return { whereClause: `${column} IS NOT NULL`, params: [] };
  }

  if (operator === 'nexists') {
    return { whereClause: `${column} IS NULL`, params: [] };
  }

  const numValue =
    typeof value === 'number' ? value : Number(String(value ?? ''));

  if (Number.isNaN(numValue)) return NOOP_SQL;

  switch (operator) {
    case 'eq':
      return { whereClause: `${column} = ?`, params: [numValue] };
    case 'neq':
      return {
        whereClause: `(${column} IS NULL OR ${column} != ?)`,
        params: [numValue],
      };
    case 'gt':
      return { whereClause: `${column} > ?`, params: [numValue] };
    case 'gte':
      return { whereClause: `${column} >= ?`, params: [numValue] };
    case 'lt':
      return { whereClause: `${column} < ?`, params: [numValue] };
    case 'lte':
      return { whereClause: `${column} <= ?`, params: [numValue] };
    default:
      return NOOP_SQL;
  }
}

// ---------------------------------------------------------------------------
// Body-only SQL translation (used by body-field search)
// ---------------------------------------------------------------------------

function translateFieldToSql(node: TqlFieldExpression): SqlTranslation {
  const column = TQL_BODY_FIELD_TO_COLUMN[node.field];

  if (!column) return NOOP_SQL;

  return translateStringOperator(column, node.operator, node.value);
}

export function translateAstToSql(node: TqlAstNode): SqlTranslation {
  switch (node.type) {
    case 'field':
      return translateFieldToSql(node);
    case 'header':
    case 'preset':
      return NOOP_SQL;
    case 'logical': {
      const left = translateAstToSql(node.left);
      const right = translateAstToSql(node.right);
      const sqlOp = node.operator === 'AND' ? 'AND' : 'OR';

      return {
        whereClause: `(${left.whereClause} ${sqlOp} ${right.whereClause})`,
        params: [...left.params, ...right.params],
      };
    }

    case 'not': {
      const inner = translateAstToSql(node.expression);

      return {
        whereClause: `NOT (${inner.whereClause})`,
        params: inner.params,
      };
    }

    case 'group': {
      const inner = translateAstToSql(node.expression);

      return { whereClause: `(${inner.whereClause})`, params: inner.params };
    }

    default:
      return NOOP_SQL;
  }
}

function translateAstToSupersetSql(node: TqlAstNode): SqlTranslation {
  switch (node.type) {
    case 'field':
      return translateFieldToSql(node);
    case 'header':
    case 'preset':
      return NOOP_SQL;
    case 'logical': {
      const left = translateAstToSupersetSql(node.left);
      const right = translateAstToSupersetSql(node.right);
      const sqlOp = node.operator === 'AND' ? 'AND' : 'OR';

      return {
        whereClause: `(${left.whereClause} ${sqlOp} ${right.whereClause})`,
        params: [...left.params, ...right.params],
      };
    }

    case 'not':
      return NOOP_SQL;
    case 'group':
      return translateAstToSupersetSql(node.expression);
    default:
      return NOOP_SQL;
  }
}

export function translateTqlToSqlSuperset(
  query: string,
): SqlTranslation | null {
  if (!query.trim()) return null;

  const result = parseTql(query);

  if (!result.success) return null;
  if (!astUsesBodyFields(result.ast)) return null;

  return translateAstToSupersetSql(result.ast);
}

// ---------------------------------------------------------------------------
// Full request-level SQL translation (metadata + body fields with aliases)
// ---------------------------------------------------------------------------

type FieldColumnMeta = { column: string; type: 'string' | 'numeric' };

const REQUEST_FIELD_TO_COLUMN: Record<string, FieldColumnMeta> = {
  'req.method': { column: 'r.method', type: 'string' },
  'req.path': { column: 'r.url', type: 'string' },
  'req.host': { column: 'r.host', type: 'string' },
  'resp.code': { column: 'r.status_code', type: 'numeric' },
  'resp.length': { column: 'r.response_body_size', type: 'numeric' },
  id: { column: 'r.id', type: 'string' },
  comment: { column: 'r.comment', type: 'string' },
  highlight: { column: 'r.highlight', type: 'string' },
  'req.body': { column: 'b.request_body', type: 'string' },
  'req.raw': { column: 'b.raw_request', type: 'string' },
  'resp.body': { column: 'b.response_body', type: 'string' },
  'resp.raw': { column: 'b.raw_response', type: 'string' },
};

function translateFieldToRequestSql(node: TqlFieldExpression): SqlTranslation {
  const meta = REQUEST_FIELD_TO_COLUMN[node.field];

  if (!meta) return NOOP_SQL;

  if (meta.type === 'numeric') {
    return translateNumericOperator(meta.column, node.operator, node.value);
  }

  return translateStringOperator(meta.column, node.operator, node.value);
}

const UNCONSTRAINED = 'unconstrained' as const;

type RequestSql = SqlTranslation | typeof UNCONSTRAINED;

function asRequestSql(translation: SqlTranslation): RequestSql {
  return translation.whereClause === NOOP_SQL.whereClause
    ? UNCONSTRAINED
    : translation;
}

function translateAstToRequestSql(
  node: TqlAstNode,
  resolvePreset?: PresetResolver,
  seenPresets: Set<string> = new Set(),
): RequestSql {
  switch (node.type) {
    case 'field':
      return asRequestSql(translateFieldToRequestSql(node));
    case 'header':
      return UNCONSTRAINED;
    case 'preset': {
      if (!resolvePreset) return UNCONSTRAINED;
      if (seenPresets.has(node.presetName)) return UNCONSTRAINED;
      const preset = resolvePreset(node.presetName);

      if (!preset) return UNCONSTRAINED;
      const parseResult = parseTql(preset.query);

      if (!parseResult.success) return UNCONSTRAINED;
      const nextSeen = new Set(seenPresets);

      nextSeen.add(node.presetName);

      return translateAstToRequestSql(parseResult.ast, resolvePreset, nextSeen);
    }

    case 'logical': {
      const left = translateAstToRequestSql(
        node.left,
        resolvePreset,
        seenPresets,
      );
      const right = translateAstToRequestSql(
        node.right,
        resolvePreset,
        seenPresets,
      );

      if (node.operator === 'OR') {
        if (left === UNCONSTRAINED || right === UNCONSTRAINED) {
          return UNCONSTRAINED;
        }

        return {
          whereClause: `(${left.whereClause} OR ${right.whereClause})`,
          params: [...left.params, ...right.params],
        };
      }

      if (left === UNCONSTRAINED) return right;
      if (right === UNCONSTRAINED) return left;

      return {
        whereClause: `(${left.whereClause} AND ${right.whereClause})`,
        params: [...left.params, ...right.params],
      };
    }

    case 'not': {
      const inner = translateAstToRequestSql(
        node.expression,
        resolvePreset,
        seenPresets,
      );

      if (inner === UNCONSTRAINED) return UNCONSTRAINED;

      return {
        whereClause: `NOT (${inner.whereClause})`,
        params: inner.params,
      };
    }

    case 'group': {
      const inner = translateAstToRequestSql(
        node.expression,
        resolvePreset,
        seenPresets,
      );

      if (inner === UNCONSTRAINED) return UNCONSTRAINED;

      return { whereClause: `(${inner.whereClause})`, params: inner.params };
    }

    default:
      return UNCONSTRAINED;
  }
}

export function translateTqlToRequestSql(
  query: string,
  resolvePreset?: PresetResolver,
): RequestSqlTranslation | null {
  if (!query.trim()) return null;

  const parseResult = parseTql(query);

  if (!parseResult.success) return null;

  const translated = translateAstToRequestSql(parseResult.ast, resolvePreset);

  if (translated === UNCONSTRAINED) return null;

  return {
    whereClause: translated.whereClause,
    params: translated.params,
    requiresBodyJoin: astUsesBodyFields(parseResult.ast, resolvePreset),
  };
}

// ---------------------------------------------------------------------------
// AST analysis helpers
// ---------------------------------------------------------------------------

export function astUsesBodyFields(
  node: TqlAstNode,
  resolvePreset?: PresetResolver,
  seenPresets: Set<string> = new Set(),
): boolean {
  switch (node.type) {
    case 'field':
      return BODY_FIELDS.has(node.field);
    case 'header':
      return false;
    case 'preset': {
      if (!resolvePreset) return false;
      if (seenPresets.has(node.presetName)) return false;
      const preset = resolvePreset(node.presetName);

      if (!preset) return false;
      const parseResult = parseTql(preset.query);

      if (!parseResult.success) return false;
      const nextSeen = new Set(seenPresets);

      nextSeen.add(node.presetName);

      return astUsesBodyFields(parseResult.ast, resolvePreset, nextSeen);
    }

    case 'logical':
      return (
        astUsesBodyFields(node.left, resolvePreset, seenPresets) ||
        astUsesBodyFields(node.right, resolvePreset, seenPresets)
      );
    case 'not':
      return astUsesBodyFields(node.expression, resolvePreset, seenPresets);
    case 'group':
      return astUsesBodyFields(node.expression, resolvePreset, seenPresets);
    default:
      return false;
  }
}

export function queryUsesBodyFields(
  query: string,
  resolvePreset?: PresetResolver,
): boolean {
  const result = parseTql(query);

  if (!result.success) return false;

  return astUsesBodyFields(result.ast, resolvePreset);
}

export function astUsesResponseBodyFields(
  node: TqlAstNode,
  resolvePreset?: PresetResolver,
  seenPresets: Set<string> = new Set(),
): boolean {
  switch (node.type) {
    case 'field':
      return RESPONSE_BODY_FIELDS.has(node.field);
    case 'header':
      return false;
    case 'preset': {
      if (!resolvePreset) return false;
      if (seenPresets.has(node.presetName)) return false;
      const preset = resolvePreset(node.presetName);

      if (!preset) return false;
      const parseResult = parseTql(preset.query);

      if (!parseResult.success) return false;
      const nextSeen = new Set(seenPresets);

      nextSeen.add(node.presetName);

      return astUsesResponseBodyFields(
        parseResult.ast,
        resolvePreset,
        nextSeen,
      );
    }

    case 'logical':
      return (
        astUsesResponseBodyFields(node.left, resolvePreset, seenPresets) ||
        astUsesResponseBodyFields(node.right, resolvePreset, seenPresets)
      );
    case 'not':
      return astUsesResponseBodyFields(
        node.expression,
        resolvePreset,
        seenPresets,
      );
    case 'group':
      return astUsesResponseBodyFields(
        node.expression,
        resolvePreset,
        seenPresets,
      );
    default:
      return false;
  }
}

export function queryUsesResponseBodyFields(
  query: string,
  resolvePreset?: PresetResolver,
): boolean {
  const result = parseTql(query);

  if (!result.success) return false;

  return astUsesResponseBodyFields(result.ast, resolvePreset);
}
